import './InviteStudents.scss';
import '../../OrganizationModals.scss';
import { Button, Input, Modal } from '@trackman/web-shared-components';

import { ReactComponent as SendIcon } from 'images/icons/Send.svg';

import { useGetClasses } from 'queryHooks/Organization/Classes/useGetClasses';
import { useGetNumberOfInvitesLeft } from 'queryHooks/Organization/Invites/useGetNumberOfInvitesLeft';
import { useSubmitInvites } from 'queryHooks/Organization/Invites/useSubmitInvites';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from 'stores/Organization/OrganizationStore';
import { useStudentsStore } from 'stores/Organization/StudentsStore';
import { NewInvite } from 'types/organization/invites';
import { NoInvitesLeft } from '../NoInvitesLeft/NoInvitesLeft';
import ErrorInfo from 'components/ErrorInfo/ErrorInfo';
import { isValidEmail } from 'utilities/inputValidation';
import { NumberOfInvitesLeft } from '../NumberOfInvitesLeft/NumberOfInvitesLeft';
import { NewInvitesList } from '../NewInvitesList/NewInvitesList';

type Props = {
  isOpen: boolean;
  close: () => void;
};

export const InviteStudents = ({ isOpen, close }: Props) => {
  const selectedOrganizationId = useOrganizationStore((state) => state.selectedOrganizationId);
  const selectedOrganizationName = useOrganizationStore((state) => state.selectedOrganizationName);
  const selectedClassId = useStudentsStore((state) => state.selectedClassId);
  const setSelectedClassId = useStudentsStore((state) => state.setSelectedClassId);

  const { data: classList } = useGetClasses(selectedOrganizationId, false, (selectedClassId) => setSelectedClassId(selectedClassId));
  const { data: numberOfInvitesLeftResponse, isPending: isNumberOfInvitesLeftPending } = useGetNumberOfInvitesLeft(selectedOrganizationId);

  const [newInvites, setNewInvites] = useState<NewInvite[]>([]);

  const submitInvites = useSubmitInvites(selectedOrganizationId, { onSuccess: () => close() });

  const getSelectedClassName = classList?.find((c) => c.id === selectedClassId)?.name ?? '';

  const handleSubmit = () => {
    submitInvites.mutate(newInvites);
  };

  return (
    <>
      {!!selectedClassId && !isNumberOfInvitesLeftPending ? (
        <InviteStudentsModal
          isOpen={isOpen}
          selectedClassId={selectedClassId}
          selectedClassName={getSelectedClassName}
          selectedOrganizationName={selectedOrganizationName}
          numberOfInvitesLeftResponse={numberOfInvitesLeftResponse || 0}
          newInvites={newInvites}
          isPending={submitInvites.isPending}
          close={close}
          confirm={handleSubmit}
          addNewInvite={(newInvite: NewInvite) => setNewInvites([...newInvites, newInvite])}
          removeNewInvite={(email: string) => setNewInvites(newInvites.filter((i) => i.email !== email))}
        />
      ) : (
        <></>
      )}
    </>
  );
};

type ModalProps = Props & {
  selectedClassId: string;
  selectedClassName: string;
  selectedOrganizationName: string;
  numberOfInvitesLeftResponse: number;
  newInvites: NewInvite[];
  isPending: boolean;
  confirm: () => void;
  addNewInvite: (newInvite: NewInvite) => void;
  removeNewInvite: (email: string) => void;
};
const InviteStudentsModal = ({
  isOpen,
  close,
  selectedClassId,
  selectedClassName,
  selectedOrganizationName,
  newInvites,
  isPending,
  numberOfInvitesLeftResponse,
  confirm,
  addNewInvite,
  removeNewInvite,
}: ModalProps) => {
  const { t } = useTranslation();
  const [inputsVisible, setInputsVisible] = useState<boolean>(true);
  const [newInviteName, setNewInviteName] = useState<string>('');
  const [newInviteEmail, setNewInviteEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [numberOfInvitesToSend, setNumberOfInvitesToSend] = useState<number>(0);
  const numberOfInvitesLeft = (numberOfInvitesLeftResponse || 0) - numberOfInvitesToSend;

  const isFormValid = newInviteEmail && newInviteName && !emailError;

  const handleIsEmailValid = (event) => {
    if (!isValidEmail(event.target.value) || event.target.value === '') {
      setEmailError(t('EmailFormatIsNotValid', { ns: 'studentsPage' }));
    } else if (newInvites.some((i) => i.email === newInviteEmail)) {
      setEmailError(t('ThisEmailIsAlreadyAdded', { ns: 'studentsPage' }));
    } else {
      setEmailError('');
    }
  };

  const handleNewInvite = () => {
    const invite: NewInvite = {
      email: newInviteEmail,
      name: newInviteName,
      classes: [{ id: selectedClassId, name: selectedClassName }],
      organisationName: selectedOrganizationName,
    };

    addNewInvite(invite);
    setInputsVisible(false);
    setNewInviteName('');
    setNewInviteEmail('');
    setNumberOfInvitesToSend((prev) => prev + 1);
  };

  const handleRemoveNewInvite = (email: string) => {
    removeNewInvite(email);
    if (newInvites.length === 1) {
      setInputsVisible(true);
    }
    setNumberOfInvitesToSend((prev) => prev - 1);
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => close()}
      className='organization-modal invite-modal'
      title={t('InviteStudents', { ns: 'studentsPage' })}
      titleIcon={<SendIcon />}
      cancelBtn={{
        label: t('Cancel', { ns: 'general' }),
        onClick: () => close(),
        className: 'styled-button secondary',
      }}
      confirmBtn={{
        label: t('SendInvitation', { ns: 'studentsPage' }),
        onClick: () => confirm(),
        disabled: numberOfInvitesLeftResponse === 0 || !newInvites.length || isPending || !!emailError,
      }}
    >
      <div className='modal-container'>
        {numberOfInvitesLeftResponse === 0 ? (
          <NoInvitesLeft />
        ) : (
          <>
            <div className='invite-form modal-body'>
              <span>
                <>{t('StudentsWillBeNotifiedToJoin', { ns: 'studentsPage', className: selectedClassName })}</>
              </span>
              <div className='invite-inputs'>
                {newInvites.length > 0 && (
                  <>
                    <NewInvitesList newInvitesList={newInvites} deleteNewInvite={handleRemoveNewInvite} />
                    {!inputsVisible && (
                      <Button
                        className='save-button styled-button secondary'
                        intent='secondary'
                        label={t('AddStudent', { ns: 'studentsPage' })}
                        disabled={numberOfInvitesLeft === 0}
                        onClick={() => setInputsVisible(true)}
                      />
                    )}
                  </>
                )}
                {inputsVisible && (
                  <>
                    <div className='inputs-wrapper'>
                      <Input
                        label={t('Name', { ns: 'studentsPage' })}
                        value={newInviteName ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewInviteName(e.currentTarget.value)}
                      />
                      <Input
                        label={t('Email', { ns: 'studentsPage' })}
                        value={newInviteEmail ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setEmailError('');
                          setNewInviteEmail(e.currentTarget.value);
                        }}
                        onBlur={handleIsEmailValid}
                      />
                      {emailError && <ErrorInfo errorMsg={emailError} />}
                    </div>
                    <Button
                      className='save-button styled-button secondary'
                      intent='secondary'
                      disabled={!isFormValid}
                      onClick={handleNewInvite}
                      label={t('Save', { ns: 'general' })}
                    />
                  </>
                )}
              </div>
            </div>
            <NumberOfInvitesLeft invitesLeft={numberOfInvitesLeft} />
          </>
        )}
      </div>
    </Modal>
  );
};
